'use client';

import { useEffect, useState } from 'react';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { ProfileSheet } from './header-profile.sheet.component';
import { ProfileSkeleton, LoginButtonSkeleton } from './header-navbar-profile.skeleton.component';

import { Button } from '@/components/ui/button/button.component';
import { getAuthSessionHint } from '@/storage/auth-session-hint.storage';
import { authStore } from '@/store/auth.store';
import { modalsOpeningStore, setOpenSignInModal } from '@/store/modal';
import { LoginOriginEnum } from '@/types/enums/analytics/login/login-analytics.enum';
import { SignInModal } from '@/app/(main)/_components/sign-in-modal/sign-in-modal.component';
import { useLevelTwoContext } from '@/feature-flags/providers/LevelTwoProvider';
import { logInfo } from '@/feature-flags/utils/logger';
import { getCorrelationContext } from '@/feature-flags/utils/correlation';

export function NavbarProfile() {
  const [mounted, setMounted] = useState(false);
  const [wasLoggedIn, setWasLoggedIn] = useState(false);
  const isLoggedIn = useStore(authStore, (state) => state.computed.isLoggedIn);
  const isAuthReady = useStore(authStore, (state) => state.computed.isAuthReady);
  const userId = useStore(authStore, (state) => state.authenticatedUser?.id);
  const openSignInModal = useStore(
    modalsOpeningStore,
    (state) => state.signInModalIsOpen,
  );
  const signInModalInitialStep = useStore(
    modalsOpeningStore,
    (state) => state.signInModalInitialStep,
  );
  const { config, variant } = useLevelTwoContext();

  useEffect(() => {
    setWasLoggedIn(getAuthSessionHint());
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) {
      return;
    }

    const loginEnabled = config.newLogin;
    const buttonVisible = !isLoggedIn && loginEnabled;

    logInfo('Login feature evaluated', {
      event: 'login_feature_evaluated',
      ...getCorrelationContext(),
      userId,
      loginEnabled,
      isLoggedIn,
      buttonVisible,
      variant,
      source: 'NavbarProfile',
    });
  }, [mounted, config.newLogin, isLoggedIn, userId, variant]);

  const handleSignIn = () => {
    setOpenSignInModal(true, { origin: LoginOriginEnum.HEADER });
  };

  if (!mounted) {
    return null;
  }

  if (config.newLogin && !isAuthReady) {
    return wasLoggedIn ? <ProfileSkeleton /> : <LoginButtonSkeleton />;
  }

  const showLoginButton = !isLoggedIn && config.newLogin;
  const showProfileSheet = isLoggedIn && config.newLogin;

  return (
    <>
      {!isLoggedIn
        ? (
            <>
              {showLoginButton && (
                <Button
                  data-testid={DataTestIdEnum.LOGIN_BUTTON}
                  variant="secondary"
                  color="primary"
                  className="text-sm lg:text-base h-8"
                  onClick={handleSignIn}
                >
                  Entrar
                </Button>
              )}
              <SignInModal
                isOpen={openSignInModal}
                setIsOpen={() => setOpenSignInModal(false)}
                initialStep={signInModalInitialStep}
              />
            </>
          )
        : (
            <>
              {showProfileSheet && <ProfileSheet />}
            </>
          )}
    </>
  );
}
